import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Package, 
  TrendingUp, 
  Download, 
  Loader, 
  MapPin, 
  Truck,
  DollarSign,
  ShoppingBag,
  User,
  Phone,
  FileText,
  Calendar
} from 'lucide-react';
import * as XLSX from 'xlsx';

function Phase5Dashboard({ userId, period, customStartDate, customEndDate }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeView, setActiveView] = useState('lima'); // 'lima' or 'provincia'
  const [expandedDelivery, setExpandedDelivery] = useState(null);

  const baseUrl = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    fetchDropshipData();
  }, [userId, period, customStartDate, customEndDate]);

  async function fetchDropshipData() {
    setLoading(true);
    try {
      const params = {
        user_id: userId,
        period: period
      };

      if (period === 'custom' && customStartDate && customEndDate) {
        params.custom_start = customStartDate;
        params.custom_end = customEndDate;
      }

      const response = await axios.get(`${baseUrl}/api/admin/dropship-deliveries`, { params });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching dropship data:', error);
    } finally {
      setLoading(false);
    }
  }

  function getStatusColor(status) {
    const colors = {
      pending: 'bg-amber-100 text-amber-700 border-amber-200',
      in_transit: 'bg-blue-100 text-blue-700 border-blue-200',
      entregado: 'bg-green-100 text-green-700 border-green-200',
      rechazado: 'bg-red-100 text-red-700 border-red-200'
    };
    return colors[status] || 'bg-slate-100 text-slate-700 border-slate-200';
  }

  function getStatusLabel(status) {
    const labels = {
      pending: 'Pendiente',
      in_transit: 'En Tránsito',
      entregado: 'Entregado',
      rechazado: 'Rechazado'
    };
    return labels[status] || status;
  }

  function downloadExcel() {
    if (!data) return;

    const deliveries = activeView === 'lima' 
      ? data.lima_deliveries 
      : data.provincia_deliveries;

    // Prepare data for Excel
    const excelData = deliveries.map(delivery => {
      const baseData = {
        'ID': delivery.id,
        'Tienda': delivery.store_name,
        'Destinatario': delivery.recipient_name,
        'Teléfono': delivery.recipient_phone,
        'Distrito': delivery.district,
        'Dirección': delivery.address,
        'Producto': delivery.product_summary,
        'Notas': delivery.notes || '',
        'Cobrado': `S/ ${delivery.charged.toFixed(2)}`,
        'Ganancia': `S/ ${delivery.earnings.toFixed(2)}`,
        'Estado': getStatusLabel(delivery.status),
        'Fecha': delivery.delivery_date ? new Date(delivery.delivery_date).toLocaleDateString('es-PE') : ''
      };

      if (activeView === 'provincia') {
        baseData['Provincia'] = delivery.province;
        baseData['Agencia'] = delivery.agency_address;
        baseData['Transporte'] = `S/ ${delivery.transport_price.toFixed(2)}`;
      } else {
        baseData['Notas Motorizado'] = delivery.notes_driver || '';
      }

      return baseData;
    });

    // Create workbook
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, activeView === 'lima' ? 'Lima' : 'Provincia');

    // Generate filename
    const filename = `Dropship_${activeView}_${new Date().toISOString().split('T')[0]}.xlsx`;

    // Download
    XLSX.writeFile(wb, filename);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-8 h-8 animate-spin text-[#B1336E]" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-600 font-montserrat">No hay datos disponibles</p>
      </div>
    );
  }

  const currentDeliveries = activeView === 'lima' 
    ? data.lima_deliveries 
    : data.provincia_deliveries;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Deliveries */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 bg-[#B1336E]/10 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-[#B1336E]" />
            </div>
            <span className="text-3xl font-extrabold text-slate-900 font-montserrat">
              {data.summary.total_deliveries}
            </span>
          </div>
          <h3 className="text-sm font-semibold text-slate-700 font-montserrat mb-2">
            Total Entregas Dropship
          </h3>
          <div className="flex gap-2 text-xs text-slate-600 font-montserrat">
            <span className="bg-[#B1336E]/10 px-2 py-1 rounded">
              Lima: {data.summary.total_lima}
            </span>
            <span className="bg-[#E6B43C]/10 px-2 py-1 rounded">
              Prov: {data.summary.total_provincia}
            </span>
          </div>
        </div>

        {/* Total Earnings */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 bg-[#67A43C]/10 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-[#67A43C]" />
            </div>
            <span className="text-3xl font-extrabold text-slate-900 font-montserrat">
              {data.summary.total_earnings.toFixed(0)}
            </span>
          </div>
          <h3 className="text-sm font-semibold text-slate-700 font-montserrat mb-2">
            Ganancia Total (S/)
          </h3>
          <p className="text-xs text-slate-600 font-montserrat">
            De productos dropship
          </p>
        </div>

        {/* Lima Earnings */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 bg-[#B1336E]/10 rounded-lg flex items-center justify-center">
              <Truck className="w-6 h-6 text-[#B1336E]" />
            </div>
            <span className="text-3xl font-extrabold text-slate-900 font-montserrat">
              {data.summary.lima_earnings.toFixed(0)}
            </span>
          </div>
          <h3 className="text-sm font-semibold text-slate-700 font-montserrat mb-2">
            Ganancia Lima (S/)
          </h3>
          <p className="text-xs text-slate-600 font-montserrat">
            {data.summary.total_lima} entregas
          </p>
        </div>

        {/* Provincia Earnings */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 bg-[#E6B43C]/10 rounded-lg flex items-center justify-center">
              <MapPin className="w-6 h-6 text-[#E6B43C]" />
            </div>
            <span className="text-3xl font-extrabold text-slate-900 font-montserrat">
              {data.summary.provincia_earnings.toFixed(0)}
            </span>
          </div>
          <h3 className="text-sm font-semibold text-slate-700 font-montserrat mb-2">
            Ganancia Provincia (S/)
          </h3>
          <p className="text-xs text-slate-600 font-montserrat">
            {data.summary.total_provincia} entregas
          </p>
        </div>
      </div>

      {/* View Selector and Download Button */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveView('lima')}
              className={`px-6 py-3 rounded-lg font-semibold font-montserrat transition-all duration-200 flex items-center gap-2 ${
                activeView === 'lima'
                  ? 'bg-[#B1336E] text-white shadow-md'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <Truck className="w-5 h-5" />
              Lima ({data.summary.total_lima})
            </button>
            <button
              onClick={() => setActiveView('provincia')}
              className={`px-6 py-3 rounded-lg font-semibold font-montserrat transition-all duration-200 flex items-center gap-2 ${
                activeView === 'provincia'
                  ? 'bg-[#E6B43C] text-white shadow-md'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <MapPin className="w-5 h-5" />
              Provincia ({data.summary.total_provincia})
            </button>
          </div>

          <button
            onClick={downloadExcel}
            disabled={currentDeliveries.length === 0}
            className="px-6 py-3 bg-[#67A43C] hover:bg-[#67A43C]/90 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg font-semibold font-montserrat transition-all duration-200 flex items-center gap-2 shadow-md"
          >
            <Download className="w-5 h-5" />
            Descargar Excel
          </button>
        </div>
      </div>

      {/* Deliveries List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-lg font-extrabold text-slate-900 font-montserrat flex items-center gap-2">
            {activeView === 'lima' ? (
              <>
                <Truck className="w-5 h-5 text-[#B1336E]" />
                Entregas Lima Dropship
              </>
            ) : (
              <>
                <MapPin className="w-5 h-5 text-[#E6B43C]" />
                Entregas Provincia Dropship
              </>
            )}
          </h2>
          <p className="text-sm text-slate-600 font-montserrat mt-1">
            {currentDeliveries.length} entrega{currentDeliveries.length !== 1 ? 's' : ''} encontrada{currentDeliveries.length !== 1 ? 's' : ''}
          </p>
        </div>

        {currentDeliveries.length === 0 ? (
          <div className="p-12 text-center">
            <ShoppingBag className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 font-montserrat">
              No hay entregas dropship para {activeView === 'lima' ? 'Lima' : 'Provincia'} en este período
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {currentDeliveries.map((delivery) => (
  <div key={delivery.id} className="p-6 hover:bg-slate-50 transition-colors">
    {/* Header Row */}
    <div className="flex items-start justify-between gap-4 mb-4">
      <div className="flex-1">
        <div className="flex items-center gap-3 flex-wrap mb-2">
          <span className="text-xs font-bold text-slate-500 font-montserrat">
            ID: {delivery.id}
          </span>
          <span
            className={`px-2 py-0.5 text-xs font-semibold rounded-full border font-montserrat ${getStatusColor(
              delivery.status
            )}`}
          >
            {getStatusLabel(delivery.status)}
          </span>
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full border bg-purple-100 text-purple-700 border-purple-200 font-montserrat">
            Dropship
          </span>
        </div>
        <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-1">
          {delivery.store_name}
        </h3>
      </div>

      {/* Earnings Badge - Conditional based on status */}
      <div className="text-right">
        <div className="text-xs text-slate-600 font-montserrat mb-1">
          {delivery.status === 'entregado' ? 'Ganancia' : 
           delivery.status === 'rechazado' ? 'Ganancia' : 
           'Ganancia Potencial'}
        </div>
        {delivery.status === 'entregado' ? (
          // Delivered - Show actual earnings in green
          <div className="text-2xl font-extrabold text-[#67A43C] font-montserrat">
            S/ {delivery.earnings.toFixed(2)}
          </div>
        ) : delivery.status === 'rechazado' ? (
          // Rejected - Show zero in red
          <div className="text-2xl font-extrabold text-red-600 font-montserrat">
            S/ 0.00
          </div>
        ) : (
          // Pending/In Transit - Show potential earnings in gray
          <div>
            <div className="text-2xl font-extrabold text-slate-400 font-montserrat">
              S/ {delivery.earnings.toFixed(2)}
            </div>
            <div className="text-xs text-slate-500 font-normal mt-0.5">
              Pendiente
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Details Grid */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      {/* Recipient Info */}
      <div className="space-y-2">
        <div className="flex items-start gap-2">
          <User className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs text-slate-600 font-montserrat">Destinatario</p>
            <p className="text-sm font-semibold text-slate-900 font-montserrat">
              {delivery.recipient_name}
            </p>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <Phone className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs text-slate-600 font-montserrat">Teléfono</p>
            <p className="text-sm font-semibold text-slate-900 font-montserrat">
              {delivery.recipient_phone}
            </p>
          </div>
        </div>
      </div>

      {/* Location Info */}
      <div className="space-y-2">
        <div className="flex items-start gap-2">
          <MapPin className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs text-slate-600 font-montserrat">
              {activeView === 'provincia' ? 'Provincia / Distrito' : 'Distrito'}
            </p>
            <p className="text-sm font-semibold text-slate-900 font-montserrat">
              {activeView === 'provincia' 
                ? `${delivery.province} / ${delivery.district}`
                : delivery.district
              }
            </p>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <MapPin className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs text-slate-600 font-montserrat">Dirección</p>
            <p className="text-sm text-slate-900 font-montserrat">
              {delivery.address}
            </p>
          </div>
        </div>
        {activeView === 'provincia' && (
          <div className="flex items-start gap-2">
            <Truck className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs text-slate-600 font-montserrat">Agencia</p>
              <p className="text-sm text-slate-900 font-montserrat">
                {delivery.agency_address}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Product Summary */}
    <div className="bg-slate-50 rounded-lg p-4 mb-4">
      <div className="flex items-start gap-2 mb-2">
        <ShoppingBag className="w-4 h-4 text-slate-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-xs text-slate-600 font-montserrat mb-1">Productos</p>
          <p className="text-sm font-semibold text-slate-900 font-montserrat">
            {delivery.product_summary}
          </p>
        </div>
      </div>

      {/* Products Detail Toggle */}
      {delivery.products_detail && delivery.products_detail.length > 0 && (
        <button
          onClick={() => setExpandedDelivery(expandedDelivery === delivery.id ? null : delivery.id)}
          className="text-xs text-[#B1336E] hover:text-[#B1336E]/80 font-semibold font-montserrat mt-2"
        >
          {expandedDelivery === delivery.id ? '▼ Ocultar detalle' : '▶ Ver detalle de productos'}
        </button>
      )}

      {/* Expanded Products Detail */}
      {expandedDelivery === delivery.id && (
        <div className="mt-3 space-y-2 border-t border-slate-200 pt-3">
          {delivery.products_detail.map((product, idx) => (
            <div key={idx} className="flex items-center justify-between text-xs">
              <div className="flex-1">
                <span className="font-semibold text-slate-900 font-montserrat">
                  {product.quantity}x
                </span>
                <span className="text-slate-700 font-montserrat ml-2">
                  {product.title}
                </span>
                <span className="text-slate-500 font-montserrat ml-2">
                  ({product.sku})
                </span>
              </div>
              <div className="text-right">
                <div className="text-slate-600 font-montserrat">
                  S/ {product.dropship_price.toFixed(2)} c/u
                </div>
                <div className={`font-bold font-montserrat ${
                  delivery.status === 'entregado' ? 'text-[#67A43C]' : 
                  delivery.status === 'rechazado' ? 'text-red-600' : 
                  'text-slate-400'
                }`}>
                  S/ {delivery.status === 'rechazado' ? '0.00' : product.earnings.toFixed(2)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>

    {/* Notes and Financial Info */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Notes */}
      <div className="space-y-2">
        {delivery.notes && (
          <div className="flex items-start gap-2">
            <FileText className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs text-slate-600 font-montserrat">Notas</p>
              <p className="text-sm text-slate-900 font-montserrat">
                {delivery.notes}
              </p>
            </div>
          </div>
        )}
        {activeView === 'lima' && delivery.notes_driver && (
          <div className="flex items-start gap-2">
            <FileText className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs text-slate-600 font-montserrat">Notas Motorizado</p>
              <p className="text-sm text-slate-900 font-montserrat">
                {delivery.notes_driver}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Financial Info */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-600 font-montserrat">Cobrado al cliente</span>
          <span className="text-sm font-bold text-slate-900 font-montserrat">
            S/ {delivery.charged.toFixed(2)}
          </span>
        </div>
        {activeView === 'provincia' && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-600 font-montserrat">Costo transporte</span>
            <span className="text-sm font-bold text-slate-900 font-montserrat">
              S/ {delivery.transport_price.toFixed(2)}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between pt-2 border-t border-slate-200">
          <span className={`text-sm font-semibold font-montserrat ${
            delivery.status === 'entregado' ? 'text-[#67A43C]' : 
            delivery.status === 'rechazado' ? 'text-red-600' : 
            'text-slate-400'
          }`}>
            {delivery.status === 'entregado' ? 'Tu ganancia' : 
             delivery.status === 'rechazado' ? 'Ganancia' : 
             'Ganancia potencial'}
          </span>
          <span className={`text-lg font-extrabold font-montserrat ${
            delivery.status === 'entregado' ? 'text-[#67A43C]' : 
            delivery.status === 'rechazado' ? 'text-red-600' : 
            'text-slate-400'
          }`}>
            S/ {delivery.status === 'rechazado' ? '0.00' : delivery.earnings.toFixed(2)}
          </span>
        </div>
        {delivery.status === 'rechazado' && (
          <p className="text-xs text-red-600 font-montserrat italic">
            * No se genera ganancia en entregas rechazadas
          </p>
        )}
        {delivery.status !== 'entregado' && delivery.status !== 'rechazado' && (
          <p className="text-xs text-slate-500 font-montserrat italic">
            * Ganancia se acredita solo cuando la entrega esté completada
          </p>
        )}
      </div>
    </div>

    {/* Date */}
    {delivery.delivery_date && (
      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-200">
        <Calendar className="w-4 h-4 text-slate-500" />
        <span className="text-xs text-slate-600 font-montserrat">
          Fecha de entrega: {new Date(delivery.delivery_date).toLocaleDateString('es-PE', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
        </span>
      </div>
    )}
  </div>
))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Phase5Dashboard;